import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, ChevronUp } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { useTheme } from '../context/ThemeContext';

const FloatingButtons = () => {
  const { language, toggleLanguage } = useLanguage();
  const { totalItems, openCart } = useCart();
  const { isDark } = useTheme();
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Show button when scrolled past menu section
      setShowScrollTop(window.scrollY > 800);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToMenu = () => {
    const menuContainer = document.getElementById('menu-container');
    if (menuContainer) {
      const offset = 100;
      const elementPosition = menuContainer.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  return (
    <>
      {/* Language Toggle - Bottom Left */}
      <motion.button
        data-testid="lang-toggle"
        onClick={toggleLanguage}
        className={`fixed bottom-6 left-6 z-40 rounded-full flex items-center p-1 shadow-2xl transition-all duration-300 ${
          isDark 
            ? 'bg-[#121212]/90 backdrop-blur-xl border border-white/10' 
            : 'bg-white/90 backdrop-blur-xl border border-black/10'
        }`}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <span 
          className={`px-3 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
            language === 'bg' 
              ? 'bg-[#008000] text-white' 
              : isDark ? 'text-white/60' : 'text-gray-500'
          }`}
        >
          BG
        </span>
        <span 
          className={`px-3 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
            language === 'en' 
              ? 'bg-[#008000] text-white' 
              : isDark ? 'text-white/60' : 'text-gray-500'
          }`}
        >
          EN
        </span>
      </motion.button>

      {/* Scroll to Menu Button - Above Cart Button */}
      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            data-testid="scroll-to-menu"
            onClick={scrollToMenu}
            className={`fixed bottom-24 right-6 z-40 w-12 h-12 rounded-full flex items-center justify-center shadow-lg transition-colors ${
              isDark 
                ? 'bg-[#121212]/90 backdrop-blur-xl border border-white/10 text-white hover:bg-white/20' 
                : 'bg-white/90 backdrop-blur-xl border border-black/10 text-gray-700 hover:bg-gray-100'
            }`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <ChevronUp size={24} />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Cart Button - Bottom Right */}
      <motion.button
        data-testid="cart-button"
        onClick={openCart}
        className="fixed bottom-6 right-6 z-40 w-14 h-14 bg-[#C8102E] text-white rounded-full flex items-center justify-center shadow-[0_4px_20px_rgba(200,16,46,0.4)] hover:bg-[#E01030] transition-colors"
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1, type: "spring" }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <ShoppingBag size={24} />
        
        {/* Badge */}
        <AnimatePresence>
          {totalItems > 0 && (
            <motion.span
              data-testid="cart-badge"
              className="absolute -top-1 -right-1 w-6 h-6 bg-[#008000] text-white text-xs font-bold rounded-full flex items-center justify-center"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              key={totalItems}
            >
              {totalItems > 9 ? '9+' : totalItems}
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>
    </>
  );
};

export default FloatingButtons;
